import { useMemo, useRef } from 'react';

import { TEXT_EDITOR_TYPE } from 'src/components/Insights';
import { getChannelFromAssetType } from 'src/data/benchmark';
import { CHANNEL, EMAILS_CHANNELS } from 'src/data/channel';
import { isLockedInsightsBlockedReason, UPGRADE_DIALOG_ACTIONS_ORIGIN } from 'src/data/common';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { DEFAULT_LANGUAGE, isHebrewLanguage } from 'src/data/generation';
import {
  doesToolSupportBenchmark,
  getToolBriefType,
  isGeneralTextGenerationTool,
} from 'src/data/generationTool';
import { getCustomerPlan, shouldUpgradeToBusiness } from 'src/data/subscription';
import * as events from 'src/lib/events';
import { injectFacebookSDK } from 'src/lib/facebook';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { useFormatProps } from 'src/services/insights/useFormatProps';
import { useBrandVoices } from 'src/services/useBrandVoices';
import { useChannelAccounts } from 'src/services/useChannelAccounts';
import { useConnectToChannel } from 'src/services/useConnectToChannel';
import { useScoringMethodName } from 'src/services/useScoringMethodName';
import { useTrackingEvent } from 'src/services/useTrackingEvent';
import {
  useCurrentCustomer,
  useCustomerTwitterSocialIntegrationEnabled,
  useCustomFeatureLimitsData,
} from 'src/store/customer';
import { useNotificationActions } from 'src/store/notification';
import { PerformanceStore } from 'src/store/performance';
import { useIsProjectReadOnly } from 'src/store/project';
import { TalkingPointsStore, useTalkingPointsActions } from 'src/store/talkingPoints';
import { useIsCurrentUserAdmin } from 'src/store/user';
import { useBenchmarkPopup } from 'src/views/BenchmarkPopup';
import { useConnectToChannelPopup } from 'src/views/ConnectToChannelPopup';
import { useUpgradePopup } from 'src/views/UpgradePopup';

import { useApplyAnalyzedToneOfVoice } from './useApplyAnalyzedToneOfVoice';
import { useBrandTermsProps } from './useBrandTermsProps';
import { useCopyInputEditMode } from './useCopyInputEditMode';
import { useSuccessMessage } from './useSuccessMessage';
import { useTabProps } from './useTabProps';
import { useToneOfVoiceProps } from './useToneOfVoiceProps';

const usePerformanceState = createStateSelectorHook(PerformanceStore, ['loadingStatusMap']);

const scoreIsNotAppealing = (score) => score < 60;

const useTalkingPointsState = createStateSelectorHook(TalkingPointsStore, ['talkingPoints']);

export const usePrepareDocumentInsightsProps = ({
  editorContent,
  editorMarkup,
  textEditorType = TEXT_EDITOR_TYPE.richSlate,
  onEditorContentChange,
  onEditorReferenceChange,
  localEditorRef,
  onCancelChanges,
  onSaveChanges,
  saveChangesEventData,
  blockedReason,
  isScoreLocked,
  readOnly,
  onScoringMethodClick,
  onApplyDetectedBrandTerm,
  onDetectedBrandTermHover,
  onDetectedBrandTermClick,
  assetType,
  scoringMethod,
  generationTool,
  updating,
  onLockedUpgradeClick,
  onUndoClick,
  undoDisabled,
  detectedBrandTermsData,
  toneOfVoiceInsightsData,
  sensitivityData,
  scoreData,
  histogramsData,
  annotationsData,
  campaignKeywords,
  forceDisabled,
  scoringMethodName,
  secondaryScoringMethodName,
  applicationMode,
  onEditorCopy,
  editorReadOnly,
  targetAudienceProps,
  onTextCopy,
  onBenchmarkApplyClick,
  benchmarkConfigProps,
  trackingEventAttributes,
  resetCounter,
  language,
  mixtureSubAssetType,
  customFormatIds,
  setSelectedFormatIdsForAssetType = undefined,
  getSelectedFormatIdsForAssetType = undefined,
}) => {
  const openBenchmarkPopup = useBenchmarkPopup();
  // Keep reference to the latest tracking event attributes, so we can use them in the event handlers
  const trackingEventAttributesRef = useRef(trackingEventAttributes);
  trackingEventAttributesRef.current = trackingEventAttributes;

  const { successMessage, setSuccessMessage } = useSuccessMessage();

  const currentCustomer = useCurrentCustomer();
  const notificationActions = useNotificationActions();
  const { brandVoices } = useBrandVoices();
  const isProjectReadOnly = useIsProjectReadOnly();
  const isCurrentUserAdmin = useIsCurrentUserAdmin();
  const noneAppeal = scoreIsNotAppealing(scoreData?.score);
  const briefType = getToolBriefType(generationTool);
  const disabled = forceDisabled || isProjectReadOnly || updating;
  const deducedScoringMethodName = useScoringMethodName(briefType, scoringMethod);
  const resourcesRowsPerCustomerFeatureLimitsData = useCustomFeatureLimitsData(
    FEATURE_LIMITS.resourcesRowsPerCustomer
  );
  const { connectedAccounts } = useChannelAccounts();
  const { loadingStatusMap } = usePerformanceState();
  const shouldIncludeTwitterSocial = useCustomerTwitterSocialIntegrationEnabled();

  const talkingPointsActions = useTalkingPointsActions();
  const trackEvent = useTrackingEvent();
  const { connectToChannel } = useConnectToChannel({
    page: events.EVENT_PAGE.editor,
  });
  const openConnectToChannelPopup = useConnectToChannelPopup();
  const { applyingAnalyzedToneOfVoice, handleApplyAnalyzedToneOfVoice } =
    useApplyAnalyzedToneOfVoice({
      generationTool,
      localEditorRef,
      onApplyingAnalyzedToneOfVoice: toneOfVoiceInsightsData?.onApplyingAnalyzedToneOfVoice,
      mixtureSubAssetType,
      language,
      setSuccessMessage,
    });
  const { editMode, setEditMode } = useCopyInputEditMode();

  // Inject SDK for FB
  injectFacebookSDK();

  const brandPolicyLoading =
    toneOfVoiceInsightsData?.loading ||
    detectedBrandTermsData?.loading ||
    sensitivityData?.loading ||
    applyingAnalyzedToneOfVoice;

  const formatProps = useFormatProps({
    localEditorRef,
    setSuccessMessage,
    setEditMode,
    readOnly,
    customFormatIds,
    isCurrentUserAdmin,
    assetType,
    setSelectedFormatIdsForAssetType,
    getSelectedFormatIdsForAssetType,
  });

  const copyInputProps = useMemo(() => {
    const handleCopyInputSaveChanges = () => {
      onSaveChanges?.();

      if (saveChangesEventData?.name) {
        trackEvent(saveChangesEventData.name, {
          ...trackingEventAttributesRef.current,
          assetType,
          generationTool,
          ...saveChangesEventData.attributes,
        });
      }
    };

    return {
      assetType,
      generationTool,
      editorContent,
      editorMarkup,
      onEditorContentChange,
      disabled,
      textEditorType,
      requireChangesConfirmation: true,
      editorRef: localEditorRef,
      onCancelChanges,
      onSaveChanges: handleCopyInputSaveChanges,
      detectedBrandTerms: detectedBrandTermsData?.items,
      applicationMode,
      onEditorCopy,
      readOnly: editorReadOnly,
      onEditorReferenceChange,
      onTextCopy,
      resetCounter,
      applyingAnalyzedToneOfVoice,
      successMessage,
      editMode,
      setEditMode,
    };
  }, [
    assetType,
    generationTool,
    editorContent,
    editorMarkup,
    onEditorContentChange,
    disabled,
    textEditorType,
    localEditorRef,
    onCancelChanges,
    detectedBrandTermsData?.items,
    applicationMode,
    onEditorCopy,
    editorReadOnly,
    onEditorReferenceChange,
    onTextCopy,
    saveChangesEventData,
    trackEvent,
    onSaveChanges,
    resetCounter,
    applyingAnalyzedToneOfVoice,
    successMessage,
    editMode,
    setEditMode,
  ]);

  const scoreDetailsProps = useMemo(
    () => ({
      score: scoreData?.score,
      basedOnValue: scoreData?.basedOn,
      loading: scoreData?.loading,
      blocked: !!blockedReason,
      locked: isScoreLocked,
      readOnly,
      displayScoringMethod: true,
      scoringMethodName: scoringMethodName || deducedScoringMethodName,
      onScoringMethodClick,
      campaignKeywords,
      language,
    }),
    [
      blockedReason,
      campaignKeywords,
      deducedScoringMethodName,
      isScoreLocked,
      onScoringMethodClick,
      readOnly,
      scoreData?.basedOn,
      scoreData?.loading,
      scoreData?.score,
      scoringMethodName,
      language,
    ]
  );

  const scoreAdditionalFactorsProps = useMemo(
    () => ({
      campaignKeywords,
      loading: annotationsData?.loading,
      headlineType: secondaryScoringMethodName,
    }),
    [annotationsData?.loading, campaignKeywords, secondaryScoringMethodName]
  );

  const histogramAgeScoreProps = useMemo(() => {
    if (isHebrewLanguage(language)) {
      return null;
    }

    return {
      loading: histogramsData?.loading,
      score: scoreData?.score,
      data: histogramsData?.age,
      noneAppeal,
    };
  }, [histogramsData?.age, histogramsData?.loading, scoreData?.score, language, noneAppeal]);

  const histogramGenderScoreProps = useMemo(() => {
    if (isHebrewLanguage(language)) {
      return null;
    }

    return {
      loading: histogramsData?.loading,
      data: histogramsData?.gender,
    };
  }, [histogramsData?.gender, histogramsData?.loading, language]);

  const { talkingPoints } = useTalkingPointsState();

  const talkingPointsProps = useMemo(() => {
    if (isHebrewLanguage(language)) {
      return null;
    }

    const isSavedTalkingPoint = (name) => talkingPoints.some((p) => p.name === name);

    const talkingPointToDisplay =
      annotationsData && Array.isArray(annotationsData.annotations)
        ? annotationsData.annotations.map((tp) => ({
            name: tp.content,
            score: tp.score,
            saved: isSavedTalkingPoint(tp.content),
          }))
        : [];

    return {
      annotations: talkingPointToDisplay,
      loading: annotationsData?.loading,
      onSave: async (talkingPoint) => {
        const success = await talkingPointsActions.saveTalkingPoints([
          {
            name: talkingPoint.name,
            score: talkingPoint.score,
          },
        ]);
        if (success) {
          notificationActions.displaySuccess('Talking points saved successfully.');
        }
      },
    };
  }, [annotationsData, language, notificationActions, talkingPoints, talkingPointsActions]);

  const toneOfVoiceProps = useToneOfVoiceProps({
    toneOfVoiceInsightsData,
    generationTool,
    applyingAnalyzedToneOfVoice,
    onApplyAnalyzedToneOfVoice: handleApplyAnalyzedToneOfVoice,
    loading: brandPolicyLoading,
    language,
    disabled,
    readOnly,
  });

  const brandTermsProps = useBrandTermsProps({
    detectedBrandTermsData,
    onApplyDetectedBrandTerm: (...args) => {
      setEditMode(true);
      onApplyDetectedBrandTerm?.(...args);
    },
    onDetectedBrandTermHover,
    onDetectedBrandTermClick,
    disabled,
    readOnly,
    loading: brandPolicyLoading,
    language: language || DEFAULT_LANGUAGE,
  });

  const channelPolicyProps = useMemo(() => {
    if (isHebrewLanguage(language)) {
      return null;
    }

    return {
      assetType,
      sensitivity: sensitivityData?.sensitivity,
      loading: brandPolicyLoading,
      policyTitle: true,
      blockedReason,
    };
  }, [assetType, brandPolicyLoading, sensitivityData?.sensitivity, language, blockedReason]);

  const blockedProps = useMemo(() => {
    if (!blockedReason) {
      return null;
    }

    return {
      blockedReason,
      blocked: isLockedInsightsBlockedReason(blockedReason),
      onUpgradeClick: onLockedUpgradeClick,
      onUndoClick,
      undoDisabled,
    };
  }, [blockedReason, onLockedUpgradeClick, onUndoClick, undoDisabled]);

  const audienceChartProps = useMemo(() => {
    if (isHebrewLanguage(language)) {
      return null;
    }

    return {
      ...targetAudienceProps,
      generationTool,
      brandVoices,
      empty: !scoreData?.loading && !(scoreData?.score > 0),
      loading: targetAudienceProps?.loading || scoreData?.loading,
      blockedReason,
    };
  }, [
    language,
    targetAudienceProps,
    generationTool,
    scoreData?.loading,
    scoreData?.score,
    brandVoices,
    blockedReason,
  ]);

  const openBenchmarkUpgradePopup = useUpgradePopup({
    actionsOrigin: UPGRADE_DIALOG_ACTIONS_ORIGIN.cipPageConnectDialog,
  });
  const benchmarkProps = useMemo(() => {
    if (!doesToolSupportBenchmark(generationTool)) {
      return null;
    }
    let channel = getChannelFromAssetType(assetType);

    if (channel === CHANNEL.twitterSocial && !shouldIncludeTwitterSocial) {
      return null;
    }

    const plan = getCustomerPlan(currentCustomer);
    const shouldUpgrade = shouldUpgradeToBusiness(plan);

    const onBenchmarkConnectClick = async () => {
      trackEvent(events.DOCUMENT.variationBenchmarkConnectToChannelClicked, {
        ...trackingEventAttributesRef.current,
        channel,
        generationTool,
      });
      if (channel === CHANNEL.emailChannel) {
        const emailChannel = await openConnectToChannelPopup({
          displayResourcesTemplates: true,
          displayEmailIntegrations: true,
          title: 'Email',
          subtitle: 'Select one of the options to import email data',
        });
        if (!EMAILS_CHANNELS.includes(emailChannel)) {
          return;
        }
        channel = emailChannel;
      }
      connectToChannel({ channel });
    };

    const onBenchmarkUpgradeClick = () => {
      trackEvent(events.DOCUMENT.variationBenchmarkUpgradeClicked, {
        ...trackingEventAttributesRef.current,
        channel,
        generationTool,
      });
    };

    const onBenchmarkPopupOpenClick = () => {
      openBenchmarkPopup({
        assetType: benchmarkConfigProps.assetType,
        channel,
        loadingBenchmark: benchmarkConfigProps.loadingBenchmark,
        onBenchmarkApplyClick,
        benchmarkConfig: benchmarkConfigProps.benchmarkConfig,
      });
      trackEvent(events.DOCUMENT.benchmarkPopupOpen, {
        ...trackingEventAttributesRef.current,
        channel,
        generationTool,
      });
    };

    return {
      onBenchmarkConnectClick,
      onBenchmarkUpgradeClick,
      channel,
      assetType,
      shouldUpgrade,
      featureLimitsData: resourcesRowsPerCustomerFeatureLimitsData,
      openPopup: onBenchmarkPopupOpenClick,
      openUpgradePopup: openBenchmarkUpgradePopup,
      connectedAccounts,
      loadingStatusMap,
      generationTool,
      ...benchmarkConfigProps,
    };
  }, [
    currentCustomer,
    onBenchmarkApplyClick,
    generationTool,
    assetType,
    benchmarkConfigProps,
    trackEvent,
    resourcesRowsPerCustomerFeatureLimitsData,
    connectToChannel,
    openConnectToChannelPopup,
    openBenchmarkUpgradePopup,
    connectedAccounts,
    loadingStatusMap,
    openBenchmarkPopup,
    shouldIncludeTwitterSocial,
  ]);

  const isGeneralTextTool = isGeneralTextGenerationTool(generationTool);

  const tabProps = useTabProps({ language, isGeneralTextTool });

  return useMemo(
    () => ({
      tabProps,
      copyInputProps,
      scoreDetailsProps: isGeneralTextTool ? null : scoreDetailsProps,
      histogramAgeScoreProps,
      histogramGenderScoreProps,
      talkingPointsProps,
      brandTermsProps,
      channelPolicyProps,
      formatProps,
      blockedProps,
      scoreAdditionalFactorsProps,
      audienceChartProps,
      benchmarkProps,
      toneOfVoiceProps,
    }),
    [
      tabProps,
      copyInputProps,
      isGeneralTextTool,
      scoreDetailsProps,
      histogramAgeScoreProps,
      histogramGenderScoreProps,
      talkingPointsProps,
      brandTermsProps,
      channelPolicyProps,
      formatProps,
      blockedProps,
      scoreAdditionalFactorsProps,
      audienceChartProps,
      benchmarkProps,
      toneOfVoiceProps,
    ]
  );
};
